import {
  isAllowedReferenceAudioFile,
  isAllowedReferenceImageFile,
  isAllowedReferenceVideoFile,
  isReferenceFileWithinSize,
} from "@/lib/reference-file-policy"
import { FLOW_NODE_TITLE_MAX_LENGTH, type FlowNodeRow } from "@/types/flow"

export type FlowFileNodeKind = "image" | "video" | "audio"

export type FlowPlacedFile = {
  file: File
  kind: FlowFileNodeKind
}

export type FlowFilePartition = {
  accepted: FlowPlacedFile[]
  rejectedType: boolean
  oversized: File | null
}

/** Картинка → image, видео → video, аудио → audio. Остальное и слишком большие файлы в accepted не попадают. */
export function partitionFlowFiles(files: File[]): FlowFilePartition {
  const accepted: FlowPlacedFile[] = []
  let rejectedType = false
  let oversized: File | null = null

  for (const file of files) {
    const kind = flowFileNodeKind(file)
    if (kind == null) {
      rejectedType = true
      continue
    }
    if (!isReferenceFileWithinSize(file)) {
      oversized ??= file
      continue
    }
    accepted.push({ file, kind })
  }

  return { accepted, rejectedType, oversized }
}

/** Имя файла без расширения, не длиннее названия ноды. «holiday.png» → «holiday». */
export function flowFileNodeTitle(name: string): string {
  const base = name.split(/[/\\]/).pop()?.trim() ?? ""
  const stripped = base.replace(/\.[^.]+$/, "").trim()
  const title = stripped || base
  return Array.from(title).slice(0, FLOW_NODE_TITLE_MAX_LENGTH).join("")
}

/** Локальная нода на время заливки. Отрицательный id не пересекается с серверными. */
export function flowUploadingNodeRow(input: {
  id: number
  kind: FlowFileNodeKind
  positionX: number
  positionY: number
  title: string
}): FlowNodeRow {
  return {
    id: input.id,
    kind: input.kind,
    position_x: input.positionX,
    position_y: input.positionY,
    display_width: null,
    prompt_text: "",
    prompt_doc: null,
    model_id: null,
    model_params: {},
    use_project_styles: false,
    generation_status: null,
    generation_error: null,
    media_url: null,
    media_thumb_url: null,
    download_url: null,
    content_type: null,
    aspect_ratio: null,
    media_width: null,
    media_height: null,
    version_count: 0,
    shot_id: null,
    shot_label: null,
    character_id: null,
    location_id: null,
    prop_id: null,
    entity_name: null,
    title: input.title || null,
    duration_ms: null,
    editor_timeline: null,
    upload_percent: 0,
  }
}

function flowFileNodeKind(file: File): FlowFileNodeKind | null {
  if (isAllowedReferenceImageFile(file)) return "image"
  if (isAllowedReferenceVideoFile(file)) return "video"
  if (isAllowedReferenceAudioFile(file)) return "audio"
  return null
}

/** Лесенка в координатах доски: шаг одинаковый на экране при любом зуме. */
export function flowFileCascadeOffset(index: number, zoom: number, screenPx = 24): number {
  const step = screenPx / (zoom > 0 ? zoom : 1)
  return index * step
}

export type FlowPasteAnchor = { id: number; x: number; y: number }

/** Каждая следующая вставка сдвигается ещё на один шаг лесенки от позиций в момент копирования. */
export function flowPastePositions(
  nodes: readonly FlowPasteAnchor[],
  pasteCount: number,
  zoom: number,
): { id: number; positionX: number; positionY: number }[] {
  const step = flowFileCascadeOffset(pasteCount, zoom)
  return nodes.map((node) => ({
    id: node.id,
    positionX: node.x + step,
    positionY: node.y + step,
  }))
}
